import {Formik} from 'formik'
import {useCallback, useRef, useState} from 'react'
import {Link} from 'react-router-dom'
import {CardHeader, Tooltip} from '../../../../modules/components'
import DataTables from '../../../../modules/DataTables'
import parseMoney from '../../../../modules/utils/parseMoney'
import {ButtonSearch} from '../../../../modules/widgets/datatable/ButtonSearch'
import {SearchMonthAt} from '../../../../modules/widgets/datatable/SearchMonthAt'
import {
    DatatableInvoiceFiltersModel,
    GetInvoiceDoctorByIdModel,
    GetInvoiceDoctorSuccessModel,
} from './models'
import {SearchDoctor} from '../../../../modules/widgets/datatable/SearchDoctor'
import generatePDF, {Margin, Options, Resolution} from 'react-to-pdf'
import axios from 'axios'
import InfoDoctorInvoiceSubpage from './subpages/InfoDoctorInvoiceSubpage'
import DoctorConsultInvoiceSubpage from './subpages/DoctorConsultInvoiceSubpage'
import {PrincipalRoutesEnum} from '../../../../routing/internal/PrincipalRouter'

export default function DatatableDoctorInvoice() {
    const initialFiltersValues = {
        doctor_id: '',
        month_at: '',
    }

    const [invoice, setInvoice] = useState<GetInvoiceDoctorSuccessModel>()
    const [filters, setFilters] = useState<DatatableInvoiceFiltersModel>(initialFiltersValues)
    const downloadRef = useRef<HTMLDivElement | null>(null)

    const getTargetElement = () => document.getElementById('download-reference')
    const options = {
        method: 'save',
        resolution: Resolution.NORMAL,
        page: {
            margin: Margin.NONE,
            format: 'A4',
            orientation: 'landscape',
        },
        canvas: {
            mimeType: 'image/jpeg',
            qualityRatio: 1,
        },
    }

    const handleDownloadPDF = async (id: string, doctorId: string, referenceAt: string) => {
        await getInvoice(id, doctorId)
        console.log(invoice)
        const element = downloadRef.current

        if (element) {
            // 1. Exibe o conteúdo removendo 'd-none'
            element.classList.remove('d-none')

            generatePDF(getTargetElement, {
                ...options,
                ...{
                    filename: `faturamento-profissional-${referenceAt.replaceAll('/', '-')}`,
                },
            } as Options)

            element.classList.add('d-none')
        }
    }

    const getInvoice = useCallback(async (id: string, doctorId: string) => {
        const {data} = await axios.get<GetInvoiceDoctorByIdModel>(
            `/core/billing/invoices/doctors/${id}/show/${doctorId}`
        )
        setInvoice(data.data)
    }, [])

    return (
        <>
            <div className='card card-custom'>
                <CardHeader
                    title='Faturamento'
                    description='Apresenta a lista de faturamentos pendentes'
                    goBack={false}
                />
                <div className='card-body'>
                    <Formik
                        initialValues={initialFiltersValues}
                        onSubmit={(values) => {
                            setFilters({
                                doctor_id: values.doctor_id,
                                month_at: values.month_at,
                            })
                        }}
                    >
                        {(props) => {
                            const {handleChange, handleBlur, handleSubmit} = props
                            return (
                                <form
                                    onSubmit={handleSubmit}
                                    id='kt_signin_change_password'
                                    noValidate
                                >
                                    <div className='row'>
                                        <SearchDoctor prop={props} />
                                        <SearchMonthAt
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />

                                        <ButtonSearch></ButtonSearch>
                                    </div>
                                </form>
                            )
                        }}
                    </Formik>
                    {!filters.doctor_id ? (
                        <div className='row mb-5'>
                            <div className='col-lg-12'>
                                <div className='alert alert-warning d-flex align-items-center m-0'>
                                    <i className='fas fa-exclamation-triangle fa-3x text-warning me-2'></i>
                                    <span className='fw-bold fs-6'>
                                        Selecione o profissional para visualizar as informações
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <DataTables
                            configs={{
                                head: [
                                    'Data',
                                    'Qtd. Consultas',
                                    'Valor Profissional',
                                    'Status',
                                    'Ações',
                                ],
                                keys: [
                                    'reference_at',
                                    'consults_count',
                                    'vn_doctor',
                                    'status',
                                    'actions',
                                ],
                                url: '/core/billing/invoices/doctors/datatable',
                                blockOrder: [
                                    'reference_at',
                                    'consults_count',
                                    'vn_doctor',
                                    'status',
                                    'actions',
                                ],
                                reverseOrder: true,
                                centeredIndex: [0, 1, 2, 3, 4, 5],
                            }}
                            filters={filters}
                            components={[
                                {
                                    target: 2,
                                    component: (obj) => (
                                        <span className='fw-bolder'>
                                            {parseMoney(obj.vn_doctor)}
                                        </span>
                                    ),
                                },
                                {
                                    target: 3,
                                    component: (obj) => (
                                        <span
                                            className={`p-2 rounded badge-light-${
                                                obj.status === 'PAID'
                                                    ? 'success'
                                                    : obj.status === 'OPENED'
                                                    ? 'info'
                                                    : 'danger'
                                            }`}
                                        >
                                            {obj.status === 'PAID'
                                                ? 'Pago'
                                                : obj.status === 'OPENED'
                                                ? 'Em aberto'
                                                : 'Pendente'}
                                        </span>
                                    ),
                                },
                                {
                                    target: 4,
                                    component: (obj) => (
                                        <div className='btn-group'>
                                            {Number(obj.id.toString()) !== 0 && (
                                                <Tooltip text='Baixar relatório' placement='top'>
                                                    <button
                                                        onClick={() => {
                                                            console.log(obj)
                                                            handleDownloadPDF(
                                                                obj.id,
                                                                filters.doctor_id,
                                                                obj.reference_at
                                                            )
                                                        }}
                                                        className={`btn btn-icon btn-light-dark btn-sm`}
                                                    >
                                                        <i className={'fas fa-file-download'}></i>
                                                    </button>
                                                </Tooltip>
                                            )}
                                            <Tooltip text='Ver detalhes' placement='top'>
                                                <Link
                                                    to={PrincipalRoutesEnum.ShowDoctorInvoice.replace(
                                                        ':id',
                                                        obj.id
                                                    ).replace(':doctorId', filters.doctor_id)}
                                                    target='_blank'
                                                    className='btn btn-icon btn-light-primary btn-sm'
                                                >
                                                    <i className='fas fa-eye' />
                                                </Link>
                                            </Tooltip>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    )}
                </div>
            </div>
            {invoice !== undefined && (
                <div className='d-none' ref={downloadRef} id='download-reference'>
                    <div className='mb-5'>
                        <InfoDoctorInvoiceSubpage
                            invoice={invoice.doctor_invoice}
                            isExport={true}
                        />
                    </div>
                    <div className='mb-5'>
                        <DoctorConsultInvoiceSubpage
                            invoiceConsults={invoice.doctor_invoice_consults}
                            isExport={true}
                        />
                    </div>
                </div>
            )}
        </>
    )
}
